import React, { useState, useRef, useEffect } from 'react';
import { navItemGroups } from './sidebar/NavItem';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import clsx from 'clsx';
import { ChevronRightIcon } from '@/assets/icons';

export default function HorizontalTabs() {
  const pathname = usePathname();

  // Get items from Management and Others sections
  const tabItems = navItemGroups
    .filter((group) => group.section === 'Management' || group.section === 'Others')
    .flatMap((group) => group.items);

  return (
    <div className="px-4 pt-1 sm:pt-2 md:px-6 lg:px-8 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-30 mb-3 sm:mb-6">
      <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto no-scrollbar">
        {tabItems.map((item) => (
          <TabItem key={item.name} item={item} pathname={pathname ?? ''} />
        ))}
      </div>
    </div>
  );
}

function TabItem({ item, pathname }: { item: any; pathname: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isActive =
    (item.name === 'Report' && (pathname === '/dashboard/report' || pathname?.startsWith('/dashboard/report/'))) ||
    item.path === pathname ||
    (item.path === '/dashboard' && pathname === '/dashboard') ||
    (item.subItems?.some((sub: any) => sub.path === pathname) ?? false);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (item.subItems) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={clsx(
            "h-12 px-4 flex items-center gap-2 border-b-2 text-sm font-medium transition-colors whitespace-nowrap",
            isActive
              ? "border-brand-500 text-gray-900 dark:text-white"
              : "border-transparent text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-gray-700"
          )}
        >
          {item.name}
          <ChevronRightIcon className={clsx("w-3 h-3 transition-transform ml-1", isOpen ? "rotate-90" : "rotate-90 opacity-50")} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-100 dark:border-gray-800 py-2 z-50">
            {item.subItems.map((sub: any) => {
              const subIsActive = pathname === sub.path;
              return (
                <Link
                  key={sub.name}
                  href={sub.path}
                  onClick={() => setIsOpen(false)}
                  className={clsx(
                    "block px-4 py-2.5 text-sm transition-colors",
                    subIsActive
                      ? "bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400 font-medium"
                      : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  )}
                >
                  {sub.name}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      href={item.path ?? '#'}
      className={clsx(
        "h-10 sm:h-12 px-3 sm:px-4 flex items-center gap-1.5 sm:gap-2 border-b-2 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap shrink-0",
        isActive
          ? "border-brand-500 text-gray-900 dark:text-white font-semibold"
          : "border-transparent text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-gray-700"
      )}
    >
      {item.name}
    </Link>
  );
}
